import * as React from 'react';
import { Timer, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OtpInput } from 'components/ui/otp-input';

export interface OtpVerifyProps {
  /** Current code value (contiguous prefix, length ≤ 6). */
  value: string;
  onChange: (value: string) => void;
  /** Fired once when all 6 digits are entered. Omit to disable auto-submit. */
  onComplete?: (value: string) => void;
  /** Inline error shown beneath the boxes (also flags the inputs invalid). */
  error?: string | null;
  disabled?: boolean;
  /** Triggers a resend; the unit owns the cooldown timer that gates it. */
  onResend: () => void;
  resendPending?: boolean;
  /** Parents bump this after a successful resend; the unit also arms once on mount. */
  resendNonce: number;
  /** Wins over the fixed cooldown, so a flow restored from storage resumes the *remaining* time. */
  cooldownUntil?: number;
  /** Cooldown length in seconds (mirrors the server's resend cooldown). */
  cooldownSeconds?: number;
  autoFocus?: boolean;
}

const formatCountdown = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

// Seconds left until an absolute deadline, or the fixed cooldown when no
// deadline is supplied (the plain resend-nonce path).
const secondsUntil = (deadline: number | undefined, fallback: number) =>
  deadline && deadline > 0 ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : fallback;

/** The cooldown lives here so both callers (signup verify, password reset) get spam protection for free. */
export const OtpVerify: React.FC<OtpVerifyProps> = ({
  value,
  onChange,
  onComplete,
  error,
  disabled,
  onResend,
  resendPending,
  resendNonce,
  cooldownUntil,
  cooldownSeconds = 60,
  autoFocus,
}) => {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = React.useState(() => secondsUntil(cooldownUntil, cooldownSeconds));

  // Arm on mount and re-arm whenever the parent signals a fresh send (a bumped
  // nonce) or hands us a new absolute deadline (e.g. a restored session).
  React.useEffect(() => {
    setSecondsLeft(secondsUntil(cooldownUntil, cooldownSeconds));
  }, [resendNonce, cooldownUntil, cooldownSeconds]);

  // Countdown ticker.
  React.useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  return (
    <div className="space-y-5">
      <div className="flex min-h-[1.25rem] items-center justify-center text-sm" aria-live="polite">
        {secondsLeft > 0 ? (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <Timer className="h-3.5 w-3.5" aria-hidden />
            {t('auth.resend_in', { time: formatCountdown(secondsLeft) })}
          </span>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={resendPending}
            className="inline-flex items-center gap-1.5 font-semibold text-foreground underline-offset-[3px] transition-opacity hover:underline disabled:opacity-50"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            {t('auth.resend_code')}
          </button>
        )}
      </div>

      <div className="flex justify-center">
        <OtpInput
          value={value}
          onChange={onChange}
          onComplete={onComplete}
          disabled={disabled}
          autoFocus={autoFocus}
          ariaInvalid={Boolean(error)}
        />
      </div>

      <p className="min-h-[1rem] text-center text-xs font-medium text-destructive" aria-live="polite">
        {error || ''}
      </p>
    </div>
  );
};

OtpVerify.displayName = 'OtpVerify';
